import React, { useEffect, useRef, useState } from 'react';
import { ErrorCode, useDropzone } from 'react-dropzone';
import styles from './UploadPatientFilesModal.module.scss';
import cx from 'classnames';
import { Alert, BlockWrapperWithSpinner, Button, Icon, Modal, Text } from 'components/index';
import { appActions } from 'store/app/slice';
import { selectCurrentPatientCase, selectPatientActionIsLoading } from 'store/patients/selectors';
import { patientsActions } from 'store/patients/slice';
import { useDispatch, useSelector } from 'store/utils';
import { File, FileType, PatientFile, PatientFileType, PatientFilesIdByType } from 'types';
import { screamingSnakeTextToLowerCase } from 'utils';

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 mb

const acceptedFormats = ['.png', '.jpg', '.jpeg', '.bmp', '.webp'];

const UploadErrors = {
  FileSizeError: 'File is too large',
  FileReaderError: 'Error reading file',
  FileTypeError: 'File type error',
};

type GroupedPatientFilesByPatientFileType = { [key in PatientFileType]: File[] };

function groupPatientFilesByType(patientFiles: PatientFile[]) {
  const result: GroupedPatientFilesByPatientFileType = Object.keys(PatientFileType).reduce(
    (res, type) => ({ ...res, [type]: [] }),
    {} as { [key in PatientFileType]: File[] },
  );

  patientFiles.forEach(({ type, file }) => result[type].push(file));

  return result;
}

export const UploadPatientFilesModal = () => {
  const dispatch = useDispatch();
  const [files, setFiles] = useState<GroupedPatientFilesByPatientFileType>();
  const [errors, setErrors] = useState<string[]>([]);
  const [showErrorModal, setShowErrorModal] = useState(false);

  const newFilesRef = useRef<(File & { data: ArrayBuffer })[]>([]);
  const lastSelectedGroupRef = useRef<PatientFileType>();
  const areFilesChanged = useRef(false);

  const currentPatient = useSelector(selectCurrentPatientCase);
  const uploadPatientFilesIsLoading = useSelector(selectPatientActionIsLoading('uploadPatientFiles'));

  useEffect(() => {
    currentPatient?.files && setFiles(groupPatientFilesByType(currentPatient.files));
  }, [currentPatient]);

  const handleFilesSelected = (plainFiles: globalThis.File[]) => {
    const filesCopy: GroupedPatientFilesByPatientFileType = structuredClone(files);

    plainFiles.map((newUploadedFile, index) => {
      if (!lastSelectedGroupRef.current) return;

      if (newUploadedFile.size > MAX_FILE_SIZE) {
        setErrors([UploadErrors.FileSizeError]);
        setShowErrorModal(true);
        return;
      }

      // if (!newUploadedFile.type.includes('image')) {
      //   setErrors([UploadErrors.FileTypeError]);
      //   setShowErrorModal(true);
      //   return;
      // }

      const localUrl = URL.createObjectURL(newUploadedFile);
      const splitFilename = newUploadedFile.name.split('.');
      const fileName =
        splitFilename.length === 2 ? splitFilename[0] : splitFilename.slice(0, splitFilename.length - 1).join('.');
      const fileExtension = splitFilename[splitFilename.length - 1];

      const fileReader = new FileReader();
      fileReader.readAsArrayBuffer(newUploadedFile);

      fileReader.addEventListener('loadend', (e) => {
        const newFile: File & { data: ArrayBuffer } = {
          id: String(newUploadedFile.lastModified) /* temp id */,
          name: fileName,
          type: newUploadedFile.type.split('/')[1].toUpperCase() as FileType,
          size: newUploadedFile.size,
          url: localUrl,
          data: fileReader.result as ArrayBuffer, // fix
          extension: fileExtension,
        };

        newFilesRef.current.push(newFile);
        lastSelectedGroupRef.current && filesCopy[lastSelectedGroupRef.current].push(newFile);

        setFiles({ ...filesCopy });
        areFilesChanged.current = true;
      });

      fileReader.addEventListener('error', () => {
        setErrors([UploadErrors.FileReaderError]);
        setShowErrorModal(true);
      });
    });
  };

  useEffect(() => {
    dispatch(patientsActions.setShouldPatientPageUpdate(false));
    dispatch(patientsActions.setIsPatientPageEdited(true));
    return () => {
      dispatch(patientsActions.setShouldPatientPageUpdate(true));
      dispatch(patientsActions.setIsPatientPageEdited(false));
      newFilesRef.current?.forEach?.((file) => URL.revokeObjectURL(file.url));
    };
  }, []);

  const handleRemoveFileClick = (fileId: string, type: PatientFileType) => {
    const newFilesIndex = newFilesRef.current.findIndex((file) => file.id === fileId);
    newFilesIndex > 0 && newFilesRef.current.splice(newFilesIndex, 1);

    const filesCopy: GroupedPatientFilesByPatientFileType = structuredClone(files);
    const allFilesIndex = filesCopy[type].findIndex((file) => file.id === fileId);
    filesCopy[type].splice(allFilesIndex, 1);

    areFilesChanged.current = true;
    setFiles(filesCopy);
  };

  const handleSave = async () => {
    if (!areFilesChanged.current || !files) {
      handleClose();
      return;
    }

    setErrors([]);
    setShowErrorModal(false);

    const allPatientFilesIdByType = {} as PatientFilesIdByType;

    Object.entries(files).map(([key, value]) => {
      const type = screamingSnakeTextToLowerCase(key) as Lowercase<keyof typeof PatientFileType>;
      allPatientFilesIdByType[type] = [];
      value.map((file) => allPatientFilesIdByType[type].push(file.id));
    });

    try {
      await dispatch(
        patientsActions.uploadPatientFiles({
          newFiles: newFilesRef.current,
          patientId: currentPatient!.id,
          allPatientFilesIdByType,
        }),
      ).unwrap();
    } catch (error: any) {
      setErrors([error]);
      setShowErrorModal(true);
    }
  };

  const onImageUploaderClick = (group: PatientFileType) => {
    lastSelectedGroupRef.current = group;
  };

  const handleClose = () => {
    !uploadPatientFilesIsLoading &&
      dispatch(appActions.setIsModalOpened({ name: 'uploadPatientFiles', isOpened: false }));
  };

  const draggedFileValidator = (file: globalThis.File) => {
    const fileType = file?.type?.split('/')[1];
    if (file.size > MAX_FILE_SIZE) {
      setErrors([UploadErrors.FileSizeError + ' : ' + (file.name || fileType || '')]);
      setShowErrorModal(true);
      return {
        code: ErrorCode.FileTooLarge,
        message: UploadErrors.FileSizeError,
      };
    }

    // if (!file.type.includes('image') || !/^.*(png|bmp|jpe?g)$/.test(file.type)) {
    //   setErrors([UploadErrors.FileTypeError + ' : ' + (file.name || fileType || '')]);
    //   setShowErrorModal(true);

    //   return {
    //     code: ErrorCode.FileInvalidType,
    //     message: UploadErrors.FileTypeError,
    //   };
    // }

    return null;
  };

  const { getRootProps, getInputProps } = useDropzone({
    // accept: { 'image/*': acceptedFormats },
    validator: draggedFileValidator,
    onDrop: handleFilesSelected,
  });

  const checkIsAllowToDelete = (createdAt: string | undefined, key: string) =>
    currentPatient?.submittedAt &&
    createdAt &&
    new Date(createdAt) <= new Date(currentPatient?.submittedAt) &&
    key === PatientFileType.COMPOSITE_VIEW;

  const handleCloseErrorModal = () => {
    setShowErrorModal(false);
    setErrors([]);
  };

  return (
    <Modal title="Images" onClose={handleClose} contentContainerElementId={styles.modalContentContainerElementId}>
      {uploadPatientFilesIsLoading && <BlockWrapperWithSpinner isLoading={uploadPatientFilesIsLoading} />}
      <div className={styles.container}>
        <Text type="body2" className={styles.description}>
          Possible file formats: PNG, JPG, JPEG, BMP. Limitations for each file is 10 MB.
        </Text>
        <div className={styles.imageTypesContainer}>
          {files &&
            Object.entries(files).map(([key, value]) => (
              <div key={key} className={cx(styles.imageTypeContainer, styles[key.toLowerCase()])}>
                <Text type="overline">{key.split('_').join(' ')}</Text>
                <div className={styles.imageList}>
                  {value.map((file) => {
                    const isAllowedToDelete = !checkIsAllowToDelete(file.createdAt, key);

                    return (
                      <div className={styles.imageListItem} key={file.url}>
                        <img src={file.url} alt={file.name} />
                        {isAllowedToDelete && (
                          <div
                            className={styles.imageListItemRemoveIcon}
                            onClick={() => handleRemoveFileClick(file.id, key as PatientFileType)}>
                            <Icon name="Close" />
                          </div>
                        )}
                      </div>
                    );
                  })}
                  <div
                    {...getRootProps({
                      className: styles.imageUploaderListItem,
                      onClick: () => onImageUploaderClick(key as PatientFileType),
                      onDrop: () => onImageUploaderClick(key as PatientFileType),
                    })}>
                    <input {...getInputProps()} />
                    <Icon name="Plus" className={styles.imageUploaderListItemPlusIcon} />
                  </div>
                </div>
              </div>
            ))}
        </div>
        {showErrorModal && (
          <div className={styles.errorContainer}>
            <div className={styles.errorModalWrapper}>
              {errors.map((error) => (
                <Alert type="error" key={error}>
                  <Text type="body2">{error}</Text>
                </Alert>
              ))}
              <Button onClick={handleCloseErrorModal}>Dismiss</Button>
            </div>
          </div>
        )}
        <div className={styles.buttonRow}>
          <Button onClick={handleClose} type="secondary" isLink>
            Cancel
          </Button>
          <Button onClick={handleSave}>Save</Button>
        </div>
      </div>
    </Modal>
  );
};
